import React, { Component } from "react";
import withStyles from "@mui/styles/withStyles";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import actions from "../../actions";
import CalendarSessions from "./CalendarSessions";
import SessionDetails from "./SessionDetails";
import styles from "./styles";
import Snackbar from "@mui/material/Snackbar";
import SessionNotesNotFoundDialog from "./SessionNotesNotFoundDialog";
import CancelSessionDialog from "./CancelSessionDialog";
import EditSelectionDialog from "./EditSelectionDialog";
import { withJoinAppointment } from "hooks/withJoinAppointment";
import {
  getUserPermissionsList,
  getVideoBreakdown,
  getVideoBreakdownLoading,
  getCancelVideoCallSuccess,
  scheduleCallLoading,
  scheduleCallSuccess,
  getWeekStartDate,
  getShowWeekCanceledCalls,
  getCallBreakdownSuccess,
  getCallBreakdownLoading,
  getUserId,
  getCompletingOffPlatform,
  getCompleteOffPlatformSuccess,
  isDirector,
  isSupervisor,
} from "../../selectors";
import CompleteOffPlatformDialog from "../CompleteOffPlatformDialog";
import { Typography } from "@mui/material";
import SessionNotesDialog from "../SessionNotes";
import {
  getCancellationReasonLabel,
  getKeyCancellationReason,
} from "../../constants/cancelReasons";
import useGetCancellationReasons from "hooks/api/appointments/useGetCancellationReasons";

const mapStateToProps = (state) => ({
  userPermissions: getUserPermissionsList(state),
  videoCallBreakdown: getVideoBreakdown(state),
  videoCallBreakdownLoading: getVideoBreakdownLoading(state),
  cancelVideoCallSuccess: getCancelVideoCallSuccess(state),
  scheduleCallLoading: scheduleCallLoading(state),
  scheduleCallSuccess: scheduleCallSuccess(state),
  weekStartDate: getWeekStartDate(state),
  showCanceledCalls: getShowWeekCanceledCalls(state),
  callBreakdownSuccess: getCallBreakdownSuccess(state),
  callBreakdownLoading: getCallBreakdownLoading(state),
  completingOffPlatform: getCompletingOffPlatform(state),
  completeOffPlatformSuccess: getCompleteOffPlatformSuccess(state),
  userId: getUserId(state),
  isDirector: isDirector(state),
  isSupervisor: isSupervisor(state),
});
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getVideoCallBreakdown: actions.getVideoCallBreakdown,
      cancelVideoCall: actions.cancelVideoCall,
      getCalendarSessions: actions.getCalendarSessions,
      clearCustomerInfo: actions.clearCustomerInfo,
      setShowWeekCanceledCalls: actions.setShowWeekCanceledCalls,
      markClientNoShow: actions.markClientNoShow,
    },
    dispatch
  );

class WeeklyCalendarSessions extends Component {
  constructor() {
    super();
    this.state = {
      sessionDetails: {},
      detailsOpen: false,
      snackBarOpen: false,
      snackBarMessage: "",
      downloadVideoCallId: null,
      noSessionNotesDialogOpen: false,
      cancelSessionOpen: false,
      cancelReasonText: "",
      cancellationReasonId: "",
      responsibleForCancellation: "",
      cancelSelectionOpen: false,
      cancelAllInstances: false,
      editSessionOpen: false,
      editSelectionOpen: false,
      editAllInstances: false,
      userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      completeOffPlatformOpen: false,
      offPlatformDetails: null,
      sessionNotesDialogOpen: false,
      currentBreakdown: [],
      enterInsuranceCode: false,
      cptCodeDialogOpen: false,
    };
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.videoCallBreakdownLoading &&
      !this.props.videoCallBreakdownLoading &&
      this.state.downloadVideoCallId
    ) {
      if (
        this.props.videoCallBreakdown.length > 0 &&
        this.props.videoCallBreakdown.some((call) => !!call.note_id)
      ) {
        this.downloadAll(this.state.downloadVideoCallId);
        this.setState({ downloadVideoCallId: null });
      } else {
        this.setState({ noSessionNotesDialogOpen: true });
      }
    }

    if (!prevProps.scheduleCallSuccess && this.props.scheduleCallSuccess) {
      this.setState({
        snackBarOpen: true,
        snackBarMessage: "Session rescheduled successfully!",
      });
    }
    if (
      prevProps.completingOffPlatform &&
      !this.props.completingOffPlatform &&
      this.state.markingClientNoShow
    ) {
      this.setState({
        markingClientNoShow: false,
        detailsOpen: false,
        snackBarOpen: true,
        snackBarMessage: "The event was marked as a no-show",
      });
    }
  }

  onMarkClientAsNoShow = (id) => {
    this.setState({ markingClientNoShow: true }, () => {
      this.props.markClientNoShow(id);
    });
  };

  // Open the session details dialog when a session is clicked
  onSelectSession = (details) => {
    const isExpired = new Date(details?.end) < new Date();
    // Don't show details for google events or expired tentative events
    if (details?.googleEvent || (isExpired && details.is_tentative)) return;
    this.setState({ sessionDetails: details });
    if (details?.billing_complete) this.setState({ sessionNotesDialogOpen: true });
    else this.setState({ detailsOpen: true });
  };

  onCloseDetailsDialog = () => {
    this.setState({ detailsOpen: false }, () => {
      setTimeout(() => {
        this.setState({ sessionDetails: {} });
      }, 100);
    });
  };

  copySessionURL = (videoKey, offPlatform) => {
    const baseUrl = process.env.BASE_PARENT_URL;
    const offPlatformURL = process.env.DOXY_URL;
    
    if (videoKey && !offPlatform) {
      const videoUrl = this.props.getVideoUrl(videoKey);
      const fullUrl = videoUrl.startsWith('http') ? videoUrl : `${baseUrl}${videoUrl}`;
      navigator.clipboard.writeText(fullUrl);
      this.setState({
        snackBarOpen: true,
        snackBarMessage: "Unique session link copied to clipboard!",
      });
    } else {
      navigator.clipboard.writeText(`${offPlatformURL}`);
      this.setState({
        snackBarOpen: true,
        snackBarMessage: "Unique session link copied to clipboard!",
      });
    }
  };

  downloadAll = async (videoCallId) => {
    const baseUrl = process.env.BASE_URL;
    location.href = `${baseUrl}/download/all-session-notes/${videoCallId}`;
    this.setState({ downloadVideoCallId: null });
  };

  downloadAllSessionNotes = (callId) => {
    this.setState({ downloadVideoCallId: callId });
    this.props.getVideoCallBreakdown(callId);
  };

  onCloseNoSessionNotesDialog = () => {
    this.setState({ noSessionNotesDialogOpen: false });
  };

  goToSessionDetails = (callId) => {
    let videoCall = "/video-calls/" + callId;
    this.props.history.push(videoCall);
  };

  stringToBoolean = (value) => {
    if (value && typeof value === "string") {
      if (value.toLowerCase() === "true") return true;
      if (value.toLowerCase() === "false") return false;
    }
    return value;
  };

  onClickCancelSession = () => {
    this.setState({ cancelSessionOpen: true });
  };

  onToggleCancelSession = () => {
    this.setState(
      {
        cancelSessionOpen: !this.state.cancelSessionOpen,
      },
      () => {
        if (!this.state.cancelSessionOpen) {
          this.setState({
            cancelReasonText: "",
            cancellationReasonId: "",
            cancelAllInstances: false,
            responsibleForCancellation: "",
          });
        }
      }
    );
  };

  onChangeCancelSelection = (e) => {
    const { value } = e.target;
    this.setState({
      cancelAllInstances: this.stringToBoolean(value),
      cancelReasonText: "",
      cancellationReasonId: "",
      responsibleForCancellation: "",
    });
  };

  onChangeCancelReason = (e) => {
    const reasons = this.state.cancelAllInstances
      ? this.props.recurringCancellationReasons
      : this.props.cancellationReasons;
    const cancellationReason = (reasons || []).find(
      (reason) => String(reason.id) === String(e.target.value)
    );
    if (cancellationReason) {
      this.setState({
        cancellationReasonId: cancellationReason.id,
        cancelReasonText: cancellationReason.displayLabel,
        responsibleForCancellation: cancellationReason.responsibleForCancellation,
      });
      return;
    }

    this.setState({ cancelReasonText: e.target.value, cancellationReasonId: "" });
  };

  onChangeResponsibleForCancellation = (e) => {
    const label = getCancellationReasonLabel(e.target.value);
    this.setState({
      responsibleForCancellation: e.target.value,
      cancelReasonText: label === e.target.value ? "" : label,
      cancellationReasonId: "",
    });
  };

  submitCancelSession = () => {
    const {
      sessionDetails,
      cancelReasonText,
      cancellationReasonId,
      cancelAllInstances,
      responsibleForCancellation,
    } = this.state;
    const { isAdmin, clinicianSessionsPageAction } = this.props;
    const activeCancellationReasons = cancelAllInstances
      ? this.props.recurringCancellationReasons
      : this.props.cancellationReasons;
    const selectedCatalogReason = (activeCancellationReasons || []).find(
      (reason) => String(reason.id) === String(cancellationReasonId)
    );
    const selectedCatalogReasonId = selectedCatalogReason?.id;
    let params = {
      message: cancelReasonText,
      responsibleForCancellation: selectedCatalogReasonId !== undefined
        ? responsibleForCancellation
        : getKeyCancellationReason(responsibleForCancellation),
      ...(selectedCatalogReasonId !== undefined
        ? { cancellationReasonId: selectedCatalogReasonId }
        : {}),
      id: sessionDetails.id,
      isAdmin,
      clinicianSessionsPageAction,
      clinicianUserId: sessionDetails.clinician_user_id,
      cancelAllInstances,
    };
    this.props.cancelVideoCall(params);
    this.onToggleCancelSession();
    this.onCloseDetailsDialog();
  };

  onClickEditSession = (recurringEvent) => {
    if (recurringEvent) {
      this.setState({ editSelectionOpen: true });
    } else {
      this.setState({ editSessionOpen: true, detailsOpen: false });
    }
  };

  onToggleEditSession = () => {
    this.setState(
      {
        editSessionOpen: !this.state.editSessionOpen,
      },
      () => {
        if (!this.state.editSessionOpen) {
          this.onCloseDetailsDialog();
          this.props.clearCustomerInfo();
        } else {
          this.setState({ detailsOpen: false });
        }
      }
    );
  };

  onToggleEditSelection = () => {
    this.setState({
      editSelectionOpen: !this.state.editSelectionOpen,
    });
  };

  onChangeEditSelection = (e) => {
    const { value } = e.target;
    this.setState({ editAllInstances: this.stringToBoolean(value) });
  };

  onContinueEditSelection = () => {
    this.onToggleEditSelection();
    this.onToggleEditSession();
  };

  onToggleCompleteOffPlatformDialog = (offPlatformDetails) => {
    this.setState({
      detailsOpen: false,
      completeOffPlatformOpen: !this.state.completeOffPlatformOpen,
      offPlatformDetails,
    });
  };

  openCptCodeDialog = async (videoCallInfo, enterCode) => {
    this.setState(
      {
        cptCodeVideoCall: videoCallInfo,
        currentBreakdown: [
          {
            ...videoCallInfo,
            client_name: `${videoCallInfo.child_first_name} ${videoCallInfo.child_last_name}`,
            insurance_code: videoCallInfo.item_type,
            billable_time_id: videoCallInfo.id,
          },
        ],
      },
      () => {
        if (enterCode) {
          this.setState({ cptCodeDialogOpen: true, enterInsuranceCode: true });
        }
      }
    );
  };

  closeCPTCodeDialog = () => {
    this.setState({
      cptCodeDialogOpen: false,
      cptCodeVideoCall: null,
    });
  };

  closeSessionNotesDialog = () => {
    this.setState({ sessionNotesDialogOpen: false }, () => {
      setTimeout(() => {
        this.setState({ sessionDetails: {} });
      }, 100);
    });
  };

  render() {
    const { classes, toggleScheduleUtilityDrawer } = this.props;
    const {
      snackBarOpen,
      snackBarMessage,
      sessionNotesDialogOpen,
      completeOffPlatformOpen,
      sessionDetails,
    } = this.state;

    return (
      <div className={classes.calendarContainer} id="WeeklyCalendarSessions">
        <CalendarSessions
          {...this.props}
          onSelectSession={this.onSelectSession}
          toggleScheduleUtilityDrawer={toggleScheduleUtilityDrawer}
          canEditEvents
        />

        <SessionDetails
          {...this.state}
          {...this.props}
          copySessionURL={this.copySessionURL}
          downloadAllSessionNotes={this.downloadAllSessionNotes}
          onCloseDetailsDialog={this.onCloseDetailsDialog}
          goToSessionDetails={this.goToSessionDetails}
          onClickCancelSession={this.onClickCancelSession}
          onClickEditSession={this.onClickEditSession}
          onToggleCompleteOffPlatformDialog={this.onToggleCompleteOffPlatformDialog}
          onMarkClientAsNoShow={this.onMarkClientAsNoShow}
        />

        <SessionNotesDialog
          open={sessionNotesDialogOpen}
          startDate={sessionDetails?.start_date}
          endDate={sessionDetails?.end_date}
          clinicianUserId={sessionDetails?.clinician_user_id}
          videoCallId={sessionDetails?.id}
          clientUserId={sessionDetails?.user_id}
          clientId={sessionDetails?.client_id}
          clientData={{
            child_name: sessionDetails?.name,
            child_last_name: sessionDetails?.child_last_name,
          }}
          onCloseCPTCodeDialog={this.closeSessionNotesDialog}
          billingType={sessionDetails?.billing_type}
          billableTimeId={sessionDetails?.billable_time_id}
          viewOnly={true}
          downloadViewOnly={true}
        />

        <Snackbar
          autoHideDuration={3000}
          onClose={() => this.setState({ snackBarOpen: false })}
          open={snackBarOpen}
        >
          <Typography>{snackBarMessage}</Typography>
        </Snackbar>

        <CancelSessionDialog
          sessionDetails={sessionDetails}
          open={this.state.cancelSessionOpen}
          cancelReasonText={this.state.cancelReasonText}
          cancellationReasonId={this.state.cancellationReasonId}
          responsibleForCancellation={this.state.responsibleForCancellation}
          closeDialog={this.onToggleCancelSession}
          onChangeCancelReason={this.onChangeCancelReason}
          onChangeResponsibleForCancellation={this.onChangeResponsibleForCancellation}
          submitCancelSession={this.submitCancelSession}
          cancelAllInstances={this.state.cancelAllInstances}
          onChangeCancelSelection={this.onChangeCancelSelection}
          cancellationReasons={this.props.cancellationReasons}
          recurringCancellationReasons={this.props.recurringCancellationReasons}
          cancellationReasonsLoading={this.props.cancellationReasonsLoading}
          recurringCancellationReasonsLoading={this.props.recurringCancellationReasonsLoading}
        />

        <EditSelectionDialog
          sessionDetails={sessionDetails}
          open={this.state.editSelectionOpen}
          editAllInstances={this.state.editAllInstances}
          closeDialog={this.onToggleEditSelection}
          onChangeEditSelection={this.onChangeEditSelection}
          onContinueEditSelection={this.onContinueEditSelection}
        />

        <SessionNotesNotFoundDialog
          open={this.state.noSessionNotesDialogOpen}
          closeDialog={this.onCloseNoSessionNotesDialog}
          classes={this.props.classes}
        />

        {completeOffPlatformOpen && (
          <CompleteOffPlatformDialog
            callDetails={this.state.offPlatformDetails}
            onToggleCompleteOffPlatformDialog={this.onToggleCompleteOffPlatformDialog}
            openCptCodeDialog={this.openCptCodeDialog}
          />
        )}
      </div>
    );
  }
}

const ConnectedWeeklyCalendarSessions = connect(
  mapStateToProps,
  mapDispatchToProps
)(
  withStyles(styles)(
    withJoinAppointment(WeeklyCalendarSessions)
  )
);

export default function WeeklyCalendarSessionsWithCancellationReasons(props) {
  const {
    cancellationReasons,
    isLoading: cancellationReasonsLoading,
  } = useGetCancellationReasons();
  const {
    cancellationReasons: recurringCancellationReasons,
    isLoading: recurringCancellationReasonsLoading,
  } = useGetCancellationReasons(true);
  return (
    <ConnectedWeeklyCalendarSessions
      {...props}
      cancellationReasons={cancellationReasons}
      recurringCancellationReasons={recurringCancellationReasons}
      cancellationReasonsLoading={cancellationReasonsLoading}
      recurringCancellationReasonsLoading={recurringCancellationReasonsLoading}
    />
  );
}
